<template>
  <div>
    <head>
      <meta charset="utf-8"/>
      <meta content="width=device-width, initial-scale=1.0" name="viewport">
      <meta content="e-Construction Application System" name="description"/>
      <link href="assets/images/favicon.png" rel="shortcut icon">
      <link href="assets/libs/dropzone/min/dropzone.min.css" rel="stylesheet" type="text/css"/>
      <link href="assets/libs/datatables.net-bs4/css/dataTables.bootstrap4.min.css" rel="stylesheet" type="text/css"/>
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css"/>
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css"/>
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css"/>
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css"/>
    </head>

    <body data-sidebar="dark">
    <div id="layout-wrapper">
      <NavigationBar/>

      <div class="main-content">
        <div class="page-content">
          <div class="container-fluid">

            <!-- Page Header -->
            <div class="row">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">
                    <i class="bx bx-building-house me-2"></i>New Construction Application
                  </h4>
                  <div class="page-title-right">
                    <ol class="breadcrumb m-0">
                      <li class="breadcrumb-item"><a href="index.html">Home</a></li>
                      <li class="breadcrumb-item"><a href="applications.html">Applications</a></li>
                      <li class="breadcrumb-item active">New Application</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <!-- Main Card -->
            <div class="row">
              <div class="col-lg-12">
                <div class="card">
                  <div class="card-body">

                    <!-- Step Progress Indicator -->
                    <div class="step-progress mb-4">
                      <div class="step-progress-bar">
                        <div class="progress" style="height: 4px;">
                          <div
                              class="progress-bar bg-primary"
                              role="progressbar"
                              :style="{ width: progressPercentage + '%' }"
                          ></div>
                        </div>
                      </div>
                      <div class="step-indicators d-flex justify-content-between mt-3">
                        <div
                            v-for="(step, index) in steps"
                            :key="index"
                            class="step-item text-center"
                            :class="{
                              'active': currentStep === index + 1,
                              'completed': currentStep > index + 1
                            }"
                            @click="goToStep(index + 1)"
                            style="cursor: pointer; flex: 1;"
                        >
                          <div
                              class="step-circle mx-auto mb-2"
                              :class="{
                                'bg-primary text-white': currentStep === index + 1,
                                'bg-success text-white': currentStep > index + 1,
                                'bg-light text-muted': currentStep < index + 1
                              }"
                          >
                            <i v-if="currentStep > index + 1" class="bx bx-check"></i>
                            <span v-else>{{ index + 1 }}</span>
                          </div>
                          <small
                              class="d-none d-md-block"
                              :class="{
                                'text-primary fw-semibold': currentStep === index + 1,
                                'text-success': currentStep > index + 1,
                                'text-muted': currentStep < index + 1
                              }"
                          >{{ step.title }}</small>
                        </div>
                      </div>
                    </div>

                    <hr class="mb-4">

                    <!-- Step 1: Application Details -->
                    <div v-show="currentStep === 1" class="step-content">
                      <div class="step-header mb-4">
                        <h4 class="text-primary mb-1">
                          <i class="bx bx-file me-2"></i>Application Details
                        </h4>
                        <p class="text-muted mb-0">Provide basic information about your development project</p>
                      </div>

                      <div class="row g-3">
                        <div class="col-md-6">
                          <label class="form-label fw-medium">
                            Development Type <span class="text-danger">*</span>
                          </label>
                          <select class="form-select form-select-lg" v-model="formData.developmentType">
                            <option value="">-- Select Development Type --</option>
                            <optgroup label="Planning">
                              <option value="subdivision">Subdivision/Amalgamation of land</option>
                              <option value="landUseChange">Change/Extension of Use of land</option>
                              <option value="leaseRenewal">Extension/Renewal of Lease of land</option>
                              <option value="advertising">Outdoor Advertising & Signage</option>
                              <option value="regularisation">Regularisation of Existing Developments</option>
                            </optgroup>
                            <optgroup label="Engineering">
                              <option value="structuralEngineering">Structural Engineering Designs & Supervision</option>
                              <option value="civilEngineering">Civil Engineering Designs, works & clearing</option>
                            </optgroup>
                            <optgroup label="Architectural">
                              <option value="newBuildings">New Buildings</option>
                              <option value="buildingAlterations">Alterations and additions to existing buildings</option>
                              <option value="demolition">Demolition of Dwellings</option>
                              <option value="architecturalRegularisation">Regularisation of Existing Developments</option>
                            </optgroup>
                          </select>
                        </div>

                        <div class="col-md-6">
                          <label class="form-label fw-medium">
                            Project Title <span class="text-danger">*</span>
                          </label>
                          <input
                              type="text"
                              class="form-control form-control-lg"
                              placeholder="Enter a descriptive project title"
                              v-model="formData.projectTitle"
                          >
                        </div>

                        <div class="col-md-6">
                          <label class="form-label fw-medium">
                            Estimated Project Value (KES)
                          </label>
                          <div class="input-group input-group-lg">
                            <span class="input-group-text">KES</span>
                            <input
                                type="text"
                                class="form-control"
                                placeholder="0.00"
                                v-model="formData.projectValue"
                            >
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Step 2: Professional Details -->
                    <div v-show="currentStep === 2" class="step-content">
                      <div class="step-header mb-4">
                        <h4 class="text-primary mb-1">
                          <i class="bx bx-user-pin me-2"></i>Professional Details
                        </h4>
                        <p class="text-muted mb-0">Search and select a registered professional for your project</p>
                      </div>

                      <div class="row g-3">
                        <!-- Professional Type Filter -->
                        <div class="col-md-4">
                          <label class="form-label fw-medium">
                            Professional Type <small class="text-muted">(Optional filter)</small>
                          </label>
                          <select
                              class="form-select"
                              v-model="formData.professionalType"
                              :disabled="selectedProfessionalId"
                          >
                            <option value="">All Types</option>
                            <option value="architect">Architect</option>
                            <option value="civilEngineer">Civil Engineer</option>
                            <option value="structuralEngineer">Structural Engineer</option>
                            <option value="electricalEngineer">Electrical Engineer</option>
                            <option value="mechanicalEngineer">Mechanical Engineer</option>
                            <option value="landSurveyor">Land Surveyor</option>
                            <option value="urbanPlanner">Urban Planner</option>
                          </select>
                        </div>

                        <!-- Professional Search -->
                        <div class="col-md-8">
                          <label class="form-label fw-medium">
                            Search Professional <span class="text-danger">*</span>
                          </label>
                          <div class="input-group">
                              <span class="input-group-text bg-light">
                                <i class="bx bx-search"></i>
                              </span>
                            <input
                                type="text"
                                class="form-control"
                                placeholder="Search by name, reg number, phone, email, ID..."
                                v-model="formData.certificateNumber"
                                autocomplete="off"
                                :disabled="selectedProfessionalId"
                                :class="{'border-success': selectedProfessionalId}"
                            >
                            <span v-if="searchingProfessional" class="input-group-text">
                                <i class="bx bx-loader bx-spin"></i>
                              </span>
                            <button
                                v-if="selectedProfessionalId"
                                class="btn btn-outline-danger"
                                type="button"
                                @click="clearSelectedProfessional"
                            >
                              <i class="bx bx-x me-1"></i> Change
                            </button>
                          </div>
                          <small v-if="!selectedProfessionalId && (!formData.certificateNumber || formData.certificateNumber.length < 2)" class="text-muted">
                            Type at least 2 characters to search
                          </small>
                          <small v-if="professionalSearchError" class="text-danger">{{ professionalSearchError }}</small>
                        </div>

                        <!-- Professional Search Results -->
                        <div v-if="showProfessionalSelection" class="col-12">
                          <div class="card border-primary shadow-sm">
                            <div class="card-header bg-primary bg-gradient text-white py-2">
                              <i class="bx bx-user me-2"></i>
                              <strong>Select a Professional</strong>
                              <span class="badge bg-white text-primary ms-2">{{ professionalSearchResults.length }} found</span>
                            </div>
                            <div class="card-body p-0">
                              <div class="table-responsive" style="max-height: 300px;">
                                <table class="table table-hover mb-0">
                                  <thead class="table-light sticky-top">
                                  <tr>
                                    <th>Reg. Number</th>
                                    <th>Name</th>
                                    <th>Type</th>
                                    <th>Contact</th>
                                    <th class="text-center" style="width: 100px;">Action</th>
                                  </tr>
                                  </thead>
                                  <tbody>
                                  <tr v-for="professional in professionalSearchResults" :key="professional.id">
                                    <td><code>{{ professional.certificate_number }}</code></td>
                                    <td>
                                      <strong>{{ professional.full_names }}</strong>
                                      <span v-if="professional.other_names" class="text-muted"> {{ professional.other_names }}</span>
                                    </td>
                                    <td><span class="badge bg-info">{{ formatProfessionalType(professional.professional_type) }}</span></td>
                                    <td>
                                      <small>{{ professional.phone_number || '-' }}</small><br>
                                      <small class="text-muted">{{ professional.email_address || '-' }}</small>
                                    </td>
                                    <td class="text-center">
                                      <button class="btn btn-sm btn-primary" @click="selectProfessional(professional)">
                                        <i class="bx bx-check"></i> Select
                                      </button>
                                    </td>
                                  </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Selected Professional Card -->
                        <div v-if="selectedProfessionalId" class="col-12">
                          <div class="card border-success bg-success bg-opacity-10">
                            <div class="card-body">
                              <div class="d-flex align-items-center">
                                <div class="avatar-md me-3">
                                  <div class="avatar-title bg-success text-white rounded-circle">
                                    <i class="bx bx-user-check font-size-24"></i>
                                  </div>
                                </div>
                                <div class="flex-grow-1">
                                  <h5 class="mb-1 text-success">Professional Selected</h5>
                                  <p class="mb-0">
                                    <strong>{{ formData.professionalFullNames }} {{ formData.professionalOtherNames }}</strong>
                                    <span class="badge bg-info ms-2">{{ formatProfessionalType(formData.professionalType) }}</span>
                                  </p>
                                  <small class="text-muted">
                                    Reg: {{ formData.certificateNumber }} |
                                    {{ formData.professionalPhoneNumber }} |
                                    {{ formData.professionalEmail }}
                                  </small>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Add New Professional Button -->
                        <div class="col-12">
                          <button type="button" class="btn btn-outline-primary" data-bs-toggle="modal" data-bs-target="#addProfessionalModal">
                            <i class="bx bx-plus-circle me-1"></i> Register New Professional
                          </button>
                        </div>
                      </div>
                    </div>

                    <!-- Step 3: Property Details -->
                    <div v-show="currentStep === 3" class="step-content">
                      <div class="step-header mb-4">
                        <h4 class="text-primary mb-1">
                          <i class="bx bx-map-pin me-2"></i>Property Details
                        </h4>
                        <p class="text-muted mb-0">Search and select the property for your development</p>
                      </div>

                      <div class="row g-3">
                        <!-- Property Search -->
                        <div class="col-12">
                          <label class="form-label fw-medium">
                            Search Property <span class="text-danger">*</span>
                          </label>
                          <div class="input-group input-group-lg">
                              <span class="input-group-text bg-light">
                                <i class="bx bx-search"></i>
                              </span>
                            <input
                                type="text"
                                class="form-control"
                                placeholder="Type plot number or owner name to search..."
                                v-model="plotNumber"
                                autocomplete="off"
                                :disabled="showPlotDetails"
                                :class="{'border-success': showPlotDetails}"
                            >
                            <span v-if="searchingPlot" class="input-group-text">
                                <i class="bx bx-loader bx-spin"></i>
                              </span>
                            <button
                                v-if="showPlotDetails"
                                class="btn btn-outline-danger"
                                type="button"
                                @click="clearSelectedProperty"
                            >
                              <i class="bx bx-x me-1"></i> Change
                            </button>
                          </div>
                          <small v-if="!showPlotDetails && (!plotNumber || plotNumber.length < 2)" class="text-muted">
                            Type at least 2 characters to search
                          </small>
                          <small v-if="plotSearchError" class="text-danger">{{ plotSearchError }}</small>
                        </div>

                        <!-- Property Search Results -->
                        <div v-if="showPropertySelection" class="col-12">
                          <div class="card border-primary shadow-sm">
                            <div class="card-header bg-primary bg-gradient text-white py-2">
                              <i class="bx bx-map me-2"></i>
                              <strong>Select a Property</strong>
                              <span class="badge bg-white text-primary ms-2">{{ totalProperties }} found</span>
                            </div>
                            <div class="card-body p-0">
                              <div class="table-responsive" style="max-height: 300px;">
                                <table class="table table-hover mb-0">
                                  <thead class="table-light sticky-top">
                                  <tr>
                                    <th>LR Number</th>
                                    <th>UPN</th>
                                    <th>Owner</th>
                                    <th>Location</th>
                                    <th class="text-end">Balance</th>
                                    <th class="text-center" style="width: 100px;">Action</th>
                                  </tr>
                                  </thead>
                                  <tbody>
                                  <tr v-for="property in propertySearchResults" :key="property.id">
                                    <td><code>{{ property.LRNumber }}</code></td>
                                    <td>{{ property.UPN }}</td>
                                    <td><strong>{{ property.ownerNames }}</strong></td>
                                    <td><small>{{ property.percelAddress || '-' }}</small></td>
                                    <td class="text-end">
                                          <span :class="parseFloat(property.currentBalance || property.outstandingArrears) > 0 ? 'text-danger fw-bold' : 'text-success'">
                                            KES {{ parseFloat(property.currentBalance || property.outstandingArrears || 0).toLocaleString() }}
                                          </span>
                                    </td>
                                    <td class="text-center">
                                      <button class="btn btn-sm btn-primary" @click="selectProperty(property)">
                                        <i class="bx bx-check"></i> Select
                                      </button>
                                    </td>
                                  </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Selected Property Details -->
                        <div v-if="showPlotDetails" class="col-12">
                          <div class="card border-success">
                            <div class="card-header bg-success bg-gradient text-white py-2">
                              <i class="bx bx-check-circle me-2"></i>
                              <strong>Property Selected</strong>
                            </div>
                            <div class="card-body">
                              <div class="row g-3">
                                <div class="col-md-3">
                                  <label class="form-label text-muted small">LR Number</label>
                                  <p class="fw-bold mb-0">{{ formData.plotDetails.LRNumber || '-' }}</p>
                                </div>
                                <div class="col-md-3">
                                  <label class="form-label text-muted small">UPN</label>
                                  <p class="fw-bold mb-0">{{ formData.plotDetails.upnNumber || '-' }}</p>
                                </div>
                                <div class="col-md-3">
                                  <label class="form-label text-muted small">Plot Size</label>
                                  <p class="fw-bold mb-0">{{ formData.plotDetails.plotSize || '-' }}</p>
                                </div>
                                <div class="col-md-3">
                                  <label class="form-label text-muted small">Current Balance</label>
                                  <p class="fw-bold mb-0" :class="parseFloat(formData.plotDetails.currentBalance) > 0 ? 'text-danger' : 'text-success'">
                                    KES {{ parseFloat(formData.plotDetails.currentBalance || 0).toLocaleString() }}
                                  </p>
                                </div>
                                <div class="col-md-6">
                                  <label class="form-label text-muted small">Owner Name</label>
                                  <p class="fw-bold mb-0">{{ formData.plotDetails.ownerFullNames || '-' }}</p>
                                </div>
                                <div class="col-md-6">
                                  <label class="form-label text-muted small">Location</label>
                                  <p class="fw-bold mb-0">{{ formData.plotDetails.ownerAddress || '-' }}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Step 4: Project Description -->
                    <div v-show="currentStep === 4" class="step-content">
                      <div class="step-header mb-4">
                        <h4 class="text-primary mb-1">
                          <i class="bx bx-text me-2"></i>Project Description
                        </h4>
                        <p class="text-muted mb-0">Provide a detailed description of your development project</p>
                      </div>

                      <div class="row g-3">
                        <div class="col-12">
                          <label class="form-label fw-medium">
                            Project Description <span class="text-danger">*</span>
                          </label>
                          <textarea
                              class="form-control"
                              rows="8"
                              placeholder="Describe your project in detail. Include information about:
• Purpose and scope of the development
• Type of construction/work to be carried out
• Expected outcomes
• Any special considerations or requirements"
                              v-model="formData.projectDescription"
                          ></textarea>
                          <div class="d-flex justify-content-between mt-2">
                            <small class="text-muted">Provide comprehensive details about your project</small>
                            <small class="text-muted">{{ (formData.projectDescription || '').length }} characters</small>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Step 5: Project Documents -->
                    <div v-show="currentStep === 5" class="step-content">
                      <div class="step-header mb-4">
                        <h4 class="text-primary mb-1">
                          <i class="bx bx-folder-open me-2"></i>Project Documents
                        </h4>
                        <p class="text-muted mb-0">Upload required documents for your application (PDF, JPG, PNG, DWG, DXF - Max 10MB each)</p>
                      </div>

                      <!-- PLUPA/DCIA Form Upload - MANDATORY -->
                      <div class="card border-primary mb-4">
                        <div class="card-header bg-primary text-white">
                          <div class="d-flex align-items-center">
                            <i class="bx bx-file font-size-24 me-2"></i>
                            <div>
                              <h5 class="mb-0 text-white">PLUPA Form (DCIA) - Development Control & Improvement Application</h5>
                              <small class="opacity-75">This is a mandatory document - Upload the scanned filled form</small>
                            </div>
                            <span v-if="plupaForm.file" class="ms-auto badge bg-success fs-6">
                                <i class="bx bx-check-circle me-1"></i> Uploaded
                              </span>
                            <span v-else class="ms-auto badge bg-warning text-dark fs-6">
                                <i class="bx bx-error-circle me-1"></i> Required
                              </span>
                          </div>
                        </div>
                        <div class="card-body">
                          <div class="row align-items-center">
                            <div class="col-md-8">
                              <p class="text-muted mb-3">
                                <i class="bx bx-info-circle me-1"></i>
                                Please upload a scanned copy of your filled and signed PLUPA/DCIA form.
                                This is the official Development Control and Improvement Application form
                                required by the Physical and Land Use Planning Act, 2019.
                              </p>
                              <div class="input-group input-group-lg">
                                  <span class="input-group-text bg-light">
                                    <i class="bx bx-upload"></i>
                                  </span>
                                <input
                                    type="file"
                                    class="form-control"
                                    :class="{'border-success': plupaForm.file, 'border-danger': !plupaForm.file && plupaFormTouched}"
                                    @change="handlePlupaFormChange"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    ref="plupaInput"
                                >
                                <button
                                    v-if="plupaForm.file"
                                    class="btn btn-outline-danger"
                                    type="button"
                                    @click="clearPlupaForm"
                                >
                                  <i class="bx bx-x"></i>
                                </button>
                              </div>
                              <div v-if="plupaForm.fileName" class="mt-2">
                                  <span class="text-success">
                                    <i class="bx bx-check-circle me-1"></i>
                                    <strong>{{ plupaForm.fileName }}</strong>
                                    ({{ (plupaForm.fileSize / 1024 / 1024).toFixed(2) }} MB)
                                  </span>
                              </div>
                              <div v-if="plupaForm.error" class="mt-2">
                                  <span class="text-danger">
                                    <i class="bx bx-error me-1"></i>{{ plupaForm.error }}
                                  </span>
                              </div>
                            </div>
                            <div class="col-md-4 text-center d-none d-md-block">
                              <div class="border rounded p-3 bg-light">
                                <i class="bx bx-file text-primary" style="font-size: 48px;"></i>
                                <p class="mb-0 mt-2 small text-muted">PLUPA/DCIA Form</p>
                                <p class="mb-0 small text-muted">Scanned Copy</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Required Document Alert -->
                      <div class="alert alert-info d-flex align-items-center mb-4">
                        <i class="bx bx-info-circle font-size-20 me-2"></i>
                        <div>
                          <strong>Also Required:</strong> Land Ownership Document must be uploaded to proceed.
                        </div>
                        <span v-if="hasLandOwnershipDoc" class="ms-auto badge bg-success">
                            <i class="bx bx-check me-1"></i> Uploaded
                          </span>
                        <span v-else class="ms-auto badge bg-warning text-dark">
                            <i class="bx bx-time me-1"></i> Pending
                          </span>
                      </div>

                      <div class="row g-3">
                        <div class="col-12">
                          <button type="button" class="btn btn-primary" @click="addDocument">
                            <i class="bx bx-plus me-1"></i> Add Document
                          </button>
                        </div>

                        <div v-if="uploadedDocuments.length === 0" class="col-12">
                          <div class="card border-dashed bg-light">
                            <div class="card-body text-center py-5">
                              <i class="bx bx-cloud-upload display-4 text-muted"></i>
                              <h5 class="mt-3 text-muted">No Documents Added</h5>
                              <p class="text-muted mb-0">Click "Add Document" to upload required files</p>
                            </div>
                          </div>
                        </div>

                        <div v-for="(doc, index) in uploadedDocuments" :key="doc.id" class="col-md-6">
                          <div class="card border h-100" :class="{'border-primary': doc.documentType === 'landOwnership'}">
                            <div class="card-header d-flex justify-content-between align-items-center py-2"
                                 :class="doc.documentType === 'landOwnership' ? 'bg-primary bg-opacity-10' : 'bg-light'">
                                <span class="fw-medium">
                                  Document {{ index + 1 }}
                                  <span v-if="doc.documentType === 'landOwnership'" class="badge bg-primary ms-2">Required</span>
                                </span>
                              <button class="btn btn-sm btn-outline-danger" @click="removeDocument(doc.id)">
                                <i class="bx bx-trash"></i>
                              </button>
                            </div>
                            <div class="card-body">
                              <div class="mb-3">
                                <label class="form-label small">Document Type <span class="text-danger">*</span></label>
                                <select class="form-select form-select-sm" v-model="doc.documentType">
                                  <option value="">Select Type</option>
                                  <option v-for="type in documentTypes" :key="type.id" :value="type.id">{{ type.name }}</option>
                                </select>
                              </div>
                              <div class="mb-3">
                                <label class="form-label small">File <span class="text-danger">*</span></label>
                                <input
                                    type="file"
                                    class="form-control form-control-sm"
                                    @change="(e) => handleDocumentChange(doc.id, e)"
                                    accept=".pdf,.jpg,.jpeg,.png,.dwg,.dxf"
                                >
                                <small v-if="doc.fileName" class="text-success">
                                  <i class="bx bx-check"></i> {{ doc.fileName }} ({{ (doc.fileSize / 1024 / 1024).toFixed(2) }} MB)
                                </small>
                                <small v-if="doc.error" class="text-danger">{{ doc.error }}</small>
                              </div>
                              <div class="row g-2">
                                <div class="col-6">
                                  <label class="form-label small">Doc Number</label>
                                  <input type="text" class="form-control form-control-sm" v-model="doc.documentNumber" placeholder="Optional">
                                </div>
                                <div class="col-6">
                                  <label class="form-label small">Expiry Date</label>
                                  <input type="date" class="form-control form-control-sm" v-model="doc.expiryDate">
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Step 6: Billing -->
                    <div v-show="currentStep === 6" class="step-content">
                      <div class="step-header mb-4">
                        <h4 class="text-primary mb-1">
                          <i class="bx bx-receipt me-2"></i>Billing Details
                        </h4>
                        <p class="text-muted mb-0">Add billing items and review your invoice</p>
                      </div>

                      <div class="row g-4">
                        <!-- Add Bill Item Form -->
                        <div class="col-lg-5">
                          <div class="card border">
                            <div class="card-header bg-light">
                              <h6 class="mb-0"><i class="bx bx-plus-circle me-2"></i>Add Bill Item</h6>
                            </div>
                            <div class="card-body">
                              <div class="mb-3">
                                <label class="form-label small">Income Type</label>
                                <select class="form-select" v-model="selectedIncomeType" :disabled="loadingIncomeTypes">
                                  <option value="">Select Income Type</option>
                                  <option v-for="type in incomeTypes" :key="type.incomeTypeId" :value="type.incomeTypeId">
                                    {{ type.incomeTypeDescription }}
                                  </option>
                                </select>
                              </div>
                              <div class="mb-3">
                                <label class="form-label small">Fee/Charge</label>
                                <select class="form-select" v-model="selectedFeeAndCharge" :disabled="!selectedIncomeType || loadingFeesAndCharges">
                                  <option value="">Select Fee/Charge</option>
                                  <option v-for="fee in feesAndCharges" :key="fee.feeId" :value="fee.feeId">
                                    {{ fee.feeDescription }} (KES {{ fee.unitFeeAmount }})
                                  </option>
                                </select>
                              </div>
                              <div class="row g-2 mb-3">
                                <div class="col-6">
                                  <label class="form-label small">Quantity</label>
                                  <input type="number" class="form-control" v-model="billingForm.quantity" min="1">
                                </div>
                                <div class="col-6">
                                  <label class="form-label small">Amount (KES)</label>
                                  <input type="number" class="form-control" v-model="billingForm.amount" readonly>
                                </div>
                              </div>
                              <button class="btn btn-primary w-100" @click="addBillItem" :disabled="!selectedFeeAndCharge">
                                <i class="bx bx-plus me-1"></i> Add Item
                              </button>
                            </div>
                          </div>
                        </div>

                        <!-- Bill Summary -->
                        <div class="col-lg-7">
                          <div class="card border">
                            <div class="card-header bg-light d-flex justify-content-between align-items-center">
                              <h6 class="mb-0"><i class="bx bx-list-ul me-2"></i>Bill Items</h6>
                              <span class="badge bg-primary">{{ billItems.length }} item(s)</span>
                            </div>
                            <div class="card-body p-0">
                              <div class="table-responsive">
                                <table class="table table-sm mb-0">
                                  <thead class="table-light">
                                  <tr>
                                    <th>#</th>
                                    <th>Description</th>
                                    <th class="text-center">Qty</th>
                                    <th class="text-end">Amount</th>
                                    <th></th>
                                  </tr>
                                  </thead>
                                  <tbody>
                                  <tr v-if="billItems.length === 0">
                                    <td colspan="5" class="text-center py-4 text-muted">
                                      <i class="bx bx-receipt display-6"></i>
                                      <p class="mb-0 mt-2">No items added yet</p>
                                    </td>
                                  </tr>
                                  <tr v-for="(item, index) in billItems" :key="item.id">
                                    <td>{{ index + 1 }}</td>
                                    <td>
                                      <strong>{{ item.feeDescription }}</strong>
                                      <br><small class="text-muted">{{ item.incomeTypeDescription }}</small>
                                    </td>
                                    <td class="text-center">{{ item.quantity }}</td>
                                    <td class="text-end">KES {{ item.totalAmount.toLocaleString() }}</td>
                                    <td>
                                      <button class="btn btn-sm btn-link text-danger p-0" @click="removeBillItem(item.id)">
                                        <i class="bx bx-trash"></i>
                                      </button>
                                    </td>
                                  </tr>
                                  </tbody>
                                  <tfoot v-if="billItems.length > 0" class="table-light">
                                  <tr>
                                    <td colspan="3" class="text-end fw-bold">Total:</td>
                                    <td class="text-end fw-bold text-primary">KES {{ calculateTotalAmount().toLocaleString() }}</td>
                                    <td></td>
                                  </tr>
                                  </tfoot>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Navigation Buttons -->
                    <div class="d-flex justify-content-between mt-4 pt-3 border-top">
                      <button
                          class="btn btn-outline-secondary btn-lg px-4"
                          :disabled="currentStep === 1"
                          @click="previousStep"
                      >
                        <i class="bx bx-chevron-left me-1"></i> Previous
                      </button>

                      <div>
                        <button
                            v-if="currentStep < totalSteps"
                            class="btn btn-primary btn-lg px-4"
                            @click="nextStep"
                        >
                          Next <i class="bx bx-chevron-right ms-1"></i>
                        </button>
                        <button
                            v-else
                            class="btn btn-success btn-lg px-4"
                            @click="submitApplication"
                            :disabled="billItems.length === 0 || isSubmitting"
                        >
                            <span v-if="isSubmitting">
                              <i class="bx bx-loader bx-spin me-1"></i> Submitting...
                            </span>
                          <span v-else>
                              <i class="bx bx-check-circle me-1"></i> Submit Application
                            </span>
                        </button>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <!-- Footer -->
        <footer class="footer">
          <div class="container-fluid">
            <div class="row">
              <div class="col-sm-6">© {{ new Date().getFullYear() }} e-Construction</div>
              <div class="col-sm-6 text-sm-end">County Government</div>
            </div>
          </div>
        </footer>
      </div>
    </div>

    <!-- Add Professional Modal -->
    <div class="modal fade" id="addProfessionalModal" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title"><i class="bx bx-user-plus me-2"></i>Register New Professional</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <div v-if="submitMessage" class="alert" :class="submitStatus === 'success' ? 'alert-success' : 'alert-danger'">
              {{ submitMessage }}
            </div>
            <form @submit.prevent="addProfessional">
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="form-label">Professional Type <span class="text-danger">*</span></label>
                  <select class="form-select" v-model="professionalForm.professionalType" required>
                    <option value="">Select Type</option>
                    <option value="architect">Architect</option>
                    <option value="civilEngineer">Civil Engineer</option>
                    <option value="structuralEngineer">Structural Engineer</option>
                    <option value="electricalEngineer">Electrical Engineer</option>
                    <option value="mechanicalEngineer">Mechanical Engineer</option>
                    <option value="landSurveyor">Land Surveyor</option>
                    <option value="urbanPlanner">Urban Planner</option>
                  </select>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Registration Number <span class="text-danger">*</span></label>
                  <input type="text" class="form-control" v-model="professionalForm.certificateNumber" required>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Full Names <span class="text-danger">*</span></label>
                  <input type="text" class="form-control" v-model="professionalForm.fullNames" required>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Other Names</label>
                  <input type="text" class="form-control" v-model="professionalForm.otherNames">
                </div>
                <div class="col-md-6">
                  <label class="form-label">Phone Number</label>
                  <input type="tel" class="form-control" v-model="professionalForm.phoneNumber">
                </div>
                <div class="col-md-6">
                  <label class="form-label">Email Address <span class="text-danger">*</span></label>
                  <input type="email" class="form-control" v-model="professionalForm.emailAddress" required>
                </div>
                <div class="col-md-6">
                  <label class="form-label">ID Number</label>
                  <input type="text" class="form-control" v-model="professionalForm.idNumber">
                </div>
                <div class="col-md-6">
                  <label class="form-label">Certification Expiry <span class="text-danger">*</span></label>
                  <input type="date" class="form-control" v-model="professionalForm.certificationExpiry" required>
                </div>
                <div class="col-12">
                  <label class="form-label">Certification File <span class="text-danger">*</span></label>
                  <input type="file" class="form-control" @change="handleProfessionalFileChange" accept=".pdf,.jpg,.jpeg,.png" required>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-primary" @click="addProfessional" :disabled="isSubmitting">
              <span v-if="isSubmitting"><i class="bx bx-loader bx-spin me-1"></i> Saving...</span>
              <span v-else><i class="bx bx-save me-1"></i> Save Professional</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Application Success Modal -->
    <div class="modal fade" id="applicationSuccessModal" tabindex="-1" data-bs-backdrop="static" data-bs-keyboard="false">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content border-0 shadow">
          <!-- Success Header -->
          <div class="modal-header border-0 bg-success text-white">
            <div class="d-flex align-items-center">
              <div class="success-checkmark me-3">
                <i class="bx bx-check-circle" style="font-size: 48px;"></i>
              </div>
              <div>
                <h4 class="modal-title mb-0 text-white">Application Submitted Successfully!</h4>
                <p class="mb-0 opacity-75">Your construction application has been received</p>
              </div>
            </div>
          </div>

          <!-- Modal Body -->
          <div class="modal-body p-4">
            <!-- Application Number Highlight -->
            <div class="text-center mb-4">
              <p class="text-muted mb-1">Application Number</p>
              <h2 class="text-primary mb-0">
                <i class="bx bx-hash"></i>{{ successData.applicationNumber }}
              </h2>
            </div>

            <!-- Application Summary -->
            <div class="card bg-light border-0 mb-4">
              <div class="card-body">
                <h6 class="card-title text-muted mb-3">
                  <i class="bx bx-list-ul me-2"></i>Application Summary
                </h6>
                <div class="row g-3">
                  <div class="col-md-6">
                    <div class="d-flex">
                      <i class="bx bx-building-house text-primary me-2 mt-1"></i>
                      <div>
                        <small class="text-muted d-block">Project Title</small>
                        <strong>{{ formData.projectTitle }}</strong>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="d-flex">
                      <i class="bx bx-category text-primary me-2 mt-1"></i>
                      <div>
                        <small class="text-muted d-block">Development Type</small>
                        <strong>{{ formatDevelopmentType(formData.developmentType) }}</strong>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="d-flex">
                      <i class="bx bx-map text-primary me-2 mt-1"></i>
                      <div>
                        <small class="text-muted d-block">Plot Number</small>
                        <strong>{{ formData.plotDetails.LRNumber }}</strong>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="d-flex">
                      <i class="bx bx-user text-primary me-2 mt-1"></i>
                      <div>
                        <small class="text-muted d-block">Property Owner</small>
                        <strong>{{ formData.plotDetails.ownerFullNames }}</strong>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="d-flex">
                      <i class="bx bx-user-pin text-primary me-2 mt-1"></i>
                      <div>
                        <small class="text-muted d-block">Professional</small>
                        <strong>{{ formData.professionalFullNames }}</strong>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="d-flex">
                      <i class="bx bx-file text-primary me-2 mt-1"></i>
                      <div>
                        <small class="text-muted d-block">Documents Uploaded</small>
                        <strong>{{ uploadedDocuments.length + (plupaForm.file ? 1 : 0) }} file(s)</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Bill Information -->
            <div class="card mb-4" :class="successData.printable ? 'border-success' : 'border-warning'">
              <div class="card-body">
                <div class="row align-items-center">
                  <div class="col-md-7">
                    <h6 class="mb-2" :class="successData.printable ? 'text-success' : 'text-warning'">
                      <i class="bx bx-receipt me-2"></i>Invoice Generated
                    </h6>
                    <p class="mb-1">
                      Bill Number: <strong class="font-size-16">{{ successData.billNo }}</strong>
                    </p>
                    <p class="mb-2">
                      Total Amount: <strong class="text-primary fs-4">KES {{ Number(successData.billAmount).toLocaleString() }}</strong>
                    </p>

                    <!-- Approval Status Badge -->
                    <div v-if="!successData.printable" class="mt-2">
                        <span class="badge bg-warning text-dark px-3 py-2">
                          <i class="bx bx-time-five me-1"></i> Pending Approval
                        </span>
                    </div>
                    <div v-else class="mt-2">
                        <span class="badge bg-success px-3 py-2">
                          <i class="bx bx-check-circle me-1"></i> Approved - Ready to Print
                        </span>
                    </div>
                  </div>
                  <div class="col-md-5 text-md-end mt-3 mt-md-0">
                    <!-- Print Button - Enabled only if approved -->
                    <button
                        v-if="successData.printable"
                        class="btn btn-success btn-lg"
                        @click="printBillFromModal"
                    >
                      <i class="bx bx-printer me-2"></i>Print Invoice
                    </button>

                    <!-- Disabled Print Button with explanation -->
                    <div v-else>
                      <button class="btn btn-secondary btn-lg" disabled>
                        <i class="bx bx-lock me-2"></i>Print Invoice
                      </button>
                      <p class="text-muted small mt-2 mb-0">
                        <i class="bx bx-info-circle me-1"></i>
                        Available after approval
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Print Application Acknowledgment Card -->
            <div class="card border-primary mb-4">
              <div class="card-body">
                <div class="row align-items-center">
                  <div class="col-md-7">
                    <h6 class="text-primary mb-2">
                      <i class="bx bx-file me-2"></i>Application Acknowledgment
                    </h6>
                    <p class="mb-0 text-muted">
                      Print your application acknowledgment as proof of submission.
                      This document contains all your application details.
                    </p>
                  </div>
                  <div class="col-md-5 text-md-end mt-3 mt-md-0">
                    <button class="btn btn-primary btn-lg" @click="printApplicationReceipt">
                      <i class="bx bx-printer me-2"></i>Print Acknowledgment
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Approval Notice -->
            <div v-if="!successData.printable" class="alert alert-warning d-flex align-items-start mb-4">
              <i class="bx bx-error-circle font-size-24 me-3 mt-1"></i>
              <div>
                <h6 class="alert-heading mb-1">Bill Pending Approval</h6>
                <p class="mb-0">
                  Your invoice has been generated but requires approval from the finance department before it can be printed.
                  You will be notified once the bill is approved and ready for payment.
                </p>
              </div>
            </div>

            <!-- Next Steps Info -->
            <div class="alert alert-info mb-0">
              <h6 class="alert-heading">
                <i class="bx bx-info-circle me-2"></i>What happens next?
              </h6>
              <ol class="mb-0 ps-3">
                <li>Your application has been submitted and is being processed</li>
                <li v-if="!successData.printable"><strong>Bill Approval:</strong> The invoice is awaiting approval from the finance department</li>
                <li>Once approved, you can print the invoice and make payment</li>
                <li>After payment, your application will proceed to the planning department for review</li>
                <li>You will receive SMS/Email notifications on status updates</li>
              </ol>
            </div>
          </div>

          <!-- Modal Footer -->
          <div class="modal-footer border-0 bg-light">
            <button type="button" class="btn btn-outline-secondary" @click="closeSuccessAndReset">
              <i class="bx bx-x me-1"></i> Close
            </button>
            <button type="button" class="btn btn-success" @click="startNewApplication">
              <i class="bx bx-plus-circle me-1"></i> New Application
            </button>
          </div>
        </div>
      </div>
    </div>

    </body>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation";
import { execute, econstruction, biller, landRate } from "@/api";

export default {
  name: "eConstructionApplication",
  components: { NavigationBar },

  data() {
    return {
      // Step Navigation
      currentStep: 1,
      totalSteps: 6,
      steps: [
        { title: 'Application', icon: 'bx-file' },
        { title: 'Professional', icon: 'bx-user-pin' },
        { title: 'Property', icon: 'bx-map-pin' },
        { title: 'Description', icon: 'bx-text' },
        { title: 'Documents', icon: 'bx-folder' },
        { title: 'Billing', icon: 'bx-receipt' }
      ],

      // Form Data
      formData: {
        developmentType: '',
        projectTitle: '',
        projectValue: '',
        projectDescription: '',
        professionalType: '',
        certificateNumber: '',
        professionalFullNames: '',
        professionalOtherNames: '',
        professionalPhoneNumber: '',
        professionalEmail: '',
        professionalIdNumber: '',
        professionalId: null,
        plotDetails: {
          id: '',
          LRNumber: '',
          upnNumber: '',
          plotSize: '',
          landValue: '',
          annualLandRateCharges: '',
          currentBalance: '',
          landType: '',
          landUse: '',
          ownerFullNames: '',
          ownerIdNumber: '',
          ownerPhoneNumber: '',
          ownerEmail: '',
          ownerKraPin: '',
          ownerAddress: '',
          subCounty: '',
          ward: '',
          zone: ''
        }
      },

      // Professional Search
      searchingProfessional: false,
      professionalSearchError: '',
      professionalSearchResults: [],
      showProfessionalSelection: false,
      selectedProfessionalId: null,
      professionalSearchDebounceTimer: null,

      // Property Search
      plotNumber: '',
      searchingPlot: false,
      plotSearchError: '',
      propertySearchResults: [],
      showPropertySelection: false,
      showPlotDetails: false,
      plotDetails: null,
      selectedPropertyId: null,
      searchDebounceTimer: null,
      totalProperties: 0,

      // Add Professional Form
      professionalForm: {
        professionalType: '',
        certificateNumber: '',
        fullNames: '',
        otherNames: '',
        phoneNumber: '',
        emailAddress: '',
        idNumber: '',
        certificationExpiry: '',
        certificationFile: null
      },

      // Document Upload
      documentTypes: [
        { id: 'landOwnership', name: 'Land Ownership Documents (Required)', required: true },
        { id: 'officialSearch', name: 'Official Search' },
        { id: 'consent', name: 'Consent Letter' },
        { id: 'architecturalDrawings', name: 'Architectural Drawings' },
        { id: 'structuralPlans', name: 'Structural Engineering Plans' },
        { id: 'approvalForms', name: 'Approval Forms' },
        { id: 'otherDocuments', name: 'Other Supporting Documents' }
      ],
      uploadedDocuments: [],

      // PLUPA/DCIA Form (Mandatory)
      plupaForm: {
        file: null,
        fileName: '',
        fileSize: 0,
        error: ''
      },
      plupaFormTouched: false,

      // Billing
      incomeTypes: [],
      feesAndCharges: [],
      loadingIncomeTypes: false,
      loadingFeesAndCharges: false,
      selectedIncomeType: null,
      selectedFeeAndCharge: null,
      billItems: [],
      billingForm: {
        quantity: 1,
        amount: 0,
        details: ''
      },

      // UI State
      isSubmitting: false,
      submitMessage: '',
      submitStatus: null,

      // Success Modal Data
      successData: {
        applicationId: '',
        applicationNumber: '',
        billNo: '',
        billAmount: 0,
        printable: false
      }
    };
  },

  computed: {
    progressPercentage() {
      return ((this.currentStep - 1) / (this.totalSteps - 1)) * 100;
    },

    hasLandOwnershipDoc() {
      return this.uploadedDocuments.some(doc =>
          doc.documentType === 'landOwnership' && doc.file
      );
    }
  },

  watch: {
    'formData.certificateNumber'(newVal) {
      if (this.selectedProfessionalId) return;

      clearTimeout(this.professionalSearchDebounceTimer);

      if (!newVal || newVal.length < 2) {
        this.professionalSearchResults = [];
        this.showProfessionalSelection = false;
        return;
      }

      this.professionalSearchDebounceTimer = setTimeout(() => {
        this.searchProfessional();
      }, 500);
    },

    plotNumber(newVal) {
      if (this.showPlotDetails) return;

      clearTimeout(this.searchDebounceTimer);

      if (!newVal || newVal.length < 2) {
        this.propertySearchResults = [];
        this.showPropertySelection = false;
        return;
      }

      this.searchDebounceTimer = setTimeout(() => {
        this.searchPlot();
      }, 500);
    },

    selectedIncomeType(newVal) {
      if (newVal) {
        this.loadFeesAndCharges(newVal);
      } else {
        this.feesAndCharges = [];
        this.selectedFeeAndCharge = null;
      }
    },

    selectedFeeAndCharge(newVal) {
      if (newVal) {
        const fee = this.feesAndCharges.find(f => String(f.feeId) === String(newVal));
        if (fee) {
          this.billingForm.amount = Number(fee.unitFeeAmount);
          this.billingForm.details = fee.feeDescription;
        }
      } else {
        this.billingForm.amount = 0;
        this.billingForm.details = '';
      }
    }
  },

  mounted() {
    this.loadIncomeTypes();
  },

  beforeDestroy() {
    clearTimeout(this.searchDebounceTimer);
    clearTimeout(this.professionalSearchDebounceTimer);
  },

  methods: {
    // Navigation
    nextStep() {
      if (!this.validateCurrentStep()) return;
      if (this.currentStep < this.totalSteps) {
        this.currentStep++;
        window.scrollTo(0, 0);
      }
    },

    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
        window.scrollTo(0, 0);
      }
    },

    goToStep(step) {
      if (step < this.currentStep) {
        this.currentStep = step;
        window.scrollTo(0, 0);
      } else if (step === this.currentStep + 1) {
        this.nextStep();
      }
    },

    validateCurrentStep() {
      const errors = [];

      switch (this.currentStep) {
        case 1:
          if (!this.formData.developmentType) errors.push("Please select a development type");
          if (!this.formData.projectTitle) errors.push("Please enter a project title");
          break;
        case 2:
          if (!this.selectedProfessionalId) errors.push("Please search and select a professional");
          break;
        case 3:
          if (!this.showPlotDetails) errors.push("Please search and select a property");
          break;
        case 4:
          if (!this.formData.projectDescription || this.formData.projectDescription.length < 20) {
            errors.push("Please provide a detailed project description (at least 20 characters)");
          }
          break;
        case 5:
          // Check for PLUPA/DCIA form (mandatory)
          this.plupaFormTouched = true;
          if (!this.plupaForm.file) {
            errors.push("PLUPA Form (DCIA) is required - Please upload the scanned filled form");
          }

          if (this.uploadedDocuments.length === 0) {
            errors.push("Please upload at least one supporting document");
          } else {
            // Check if Land Ownership Document is uploaded
            const hasLandOwnership = this.uploadedDocuments.some(doc =>
                doc.documentType === 'landOwnership' && doc.file
            );
            if (!hasLandOwnership) {
              errors.push("Land Ownership Document is required");
            }

            for (const doc of this.uploadedDocuments) {
              if (!doc.documentType) errors.push("Please select document type for all documents");
              if (!doc.file) errors.push("Please select a file for all documents");
            }
          }
          break;
      }

      if (errors.length > 0) {
        alert('Please fix the following:\n• ' + errors.join('\n• '));
        return false;
      }
      return true;
    },

    // Professional Search
    searchProfessional() {
      if (!this.formData.certificateNumber || this.formData.certificateNumber.length < 2) return;

      this.searchingProfessional = true;
      this.professionalSearchError = '';

      const data = new FormData();
      data.append("function", "searchProfessional");
      data.append("keyword", this.formData.certificateNumber);

      execute(data, econstruction)
          .then((res) => {
            this.searchingProfessional = false;
            if (res.data.success && res.data.data?.professional) {
              const professionals = Array.isArray(res.data.data.professional)
                  ? res.data.data.professional
                  : [res.data.data.professional];

              // Filter by professional type if selected
              let filteredResults = professionals;
              if (this.formData.professionalType) {
                filteredResults = professionals.filter(p =>
                    p.professional_type === this.formData.professionalType
                );
              }

              if (filteredResults.length === 0) {
                this.professionalSearchError = 'No professionals found matching your criteria';
                this.showProfessionalSelection = false;
              } else if (filteredResults.length === 1) {
                this.selectProfessional(filteredResults[0]);
              } else {
                this.professionalSearchResults = filteredResults;
                this.showProfessionalSelection = true;
              }
            } else {
              this.professionalSearchError = res.data.message || 'No professionals found';
              this.showProfessionalSelection = false;
            }
          })
          .catch((e) => {
            this.searchingProfessional = false;
            this.professionalSearchError = e.message || 'Search failed';
          });
    },

    selectProfessional(professional) {
      this.selectedProfessionalId = professional.id;
      this.showProfessionalSelection = false;
      this.formData.professionalType = professional.professional_type || '';
      this.formData.certificateNumber = professional.certificate_number || '';
      this.formData.professionalFullNames = professional.full_names || '';
      this.formData.professionalOtherNames = professional.other_names || '';
      this.formData.professionalPhoneNumber = professional.phone_number || '';
      this.formData.professionalEmail = professional.email_address || '';
      this.formData.professionalIdNumber = professional.id_number || '';
      this.formData.professionalId = professional.id;
    },

    clearSelectedProfessional() {
      this.selectedProfessionalId = null;
      this.formData.certificateNumber = '';
      this.formData.professionalFullNames = '';
      this.formData.professionalOtherNames = '';
      this.formData.professionalPhoneNumber = '';
      this.formData.professionalEmail = '';
      this.formData.professionalIdNumber = '';
      this.formData.professionalId = null;
      this.professionalSearchResults = [];
      this.showProfessionalSelection = false;
    },

    formatProfessionalType(type) {
      const types = {
        'architect': 'Architect',
        'civilEngineer': 'Civil Engineer',
        'structuralEngineer': 'Structural Engineer',
        'electricalEngineer': 'Electrical Engineer',
        'mechanicalEngineer': 'Mechanical Engineer',
        'landSurveyor': 'Land Surveyor',
        'urbanPlanner': 'Urban Planner'
      };
      return types[type] || type;
    },

    // Property Search
    searchPlot() {
      if (!this.plotNumber || this.plotNumber.length < 2) return;

      this.searchingPlot = true;
      this.plotSearchError = '';

      const data = new FormData();
      data.append("function", "getLandRates");
      data.append("search", this.plotNumber);
      data.append("page", 1);
      data.append("rows_per_page", 10);
      data.append("dateFrom", "");
      data.append("dateTo", "");
      data.append("subCountyName", "");
      data.append("wardName", "");

      execute(data, landRate)
          .then((res) => {
            this.searchingPlot = false;
            if (res.data.success && res.data.data?.properties) {
              const properties = res.data.data.properties;
              this.totalProperties = res.data.data.count?.count || properties.length;

              if (properties.length === 1) {
                this.selectProperty(properties[0]);
              } else if (properties.length > 0) {
                this.propertySearchResults = properties;
                this.showPropertySelection = true;
              } else {
                this.plotSearchError = 'No properties found';
              }
            } else {
              this.plotSearchError = res.data.message || 'No properties found';
            }
          })
          .catch((e) => {
            this.searchingPlot = false;
            this.plotSearchError = e.message || 'Search failed';
          });
    },

    selectProperty(property) {
      this.plotDetails = property;
      this.showPlotDetails = true;
      this.showPropertySelection = false;
      this.selectedPropertyId = property.id;

      this.formData.plotDetails = {
        id: property.id,
        LRNumber: property.LRNumber,
        upnNumber: property.UPN || '',
        plotSize: property.percelSize || '',
        currentBalance: property.currentBalance || property.outstandingArrears || '0',
        landType: property.block || '',
        landUse: property.useName || '',
        ownerFullNames: property.ownerNames || '',
        ownerIdNumber: property.ownerID || '',
        ownerPhoneNumber: property.ownerPhoneNo || '',
        ownerEmail: property.ownerEmail || '',
        ownerKraPin: property.pinNumber || '',
        ownerAddress: property.percelAddress || '',
        subCounty: property.subCountyID || '',
        ward: property.wardID || '',
        zone: property.zone || ''
      };
    },

    clearSelectedProperty() {
      this.showPlotDetails = false;
      this.plotDetails = null;
      this.selectedPropertyId = null;
      this.plotNumber = '';
      this.propertySearchResults = [];
      this.showPropertySelection = false;
      this.formData.plotDetails = {
        id: '', LRNumber: '', upnNumber: '', plotSize: '', currentBalance: '',
        landType: '', landUse: '', ownerFullNames: '', ownerIdNumber: '',
        ownerPhoneNumber: '', ownerEmail: '', ownerKraPin: '', ownerAddress: '',
        subCounty: '', ward: '', zone: ''
      };
    },

    // Document Management
    addDocument() {
      // Pre-select Land Ownership if not already added
      const hasLandOwnership = this.uploadedDocuments.some(d => d.documentType === 'landOwnership');
      const defaultType = !hasLandOwnership ? 'landOwnership' : '';

      this.uploadedDocuments.push({
        id: Date.now(),
        documentType: defaultType,
        documentNumber: '',
        expiryDate: '',
        file: null,
        fileName: '',
        fileSize: 0,
        error: ''
      });
    },

    removeDocument(docId) {
      this.uploadedDocuments = this.uploadedDocuments.filter(d => d.id !== docId);
    },

    handleDocumentChange(docId, event) {
      const doc = this.uploadedDocuments.find(d => d.id === docId);
      if (!doc) return;

      const file = event.target.files[0];
      if (!file) return;

      if (file.size > 10 * 1024 * 1024) {
        doc.error = 'File exceeds 10MB limit';
        return;
      }

      doc.file = file;
      doc.fileName = file.name;
      doc.fileSize = file.size;
      doc.error = '';
    },

    // PLUPA/DCIA Form Handling
    handlePlupaFormChange(event) {
      this.plupaFormTouched = true;
      const file = event.target.files[0];

      if (!file) {
        this.plupaForm.file = null;
        this.plupaForm.fileName = '';
        this.plupaForm.fileSize = 0;
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        this.plupaForm.error = 'File exceeds 10MB limit';
        this.plupaForm.file = null;
        return;
      }

      this.plupaForm.file = file;
      this.plupaForm.fileName = file.name;
      this.plupaForm.fileSize = file.size;
      this.plupaForm.error = '';
    },

    clearPlupaForm() {
      this.plupaForm.file = null;
      this.plupaForm.fileName = '';
      this.plupaForm.fileSize = 0;
      this.plupaForm.error = '';
      if (this.$refs.plupaInput) {
        this.$refs.plupaInput.value = '';
      }
    },

    // Billing
    loadIncomeTypes() {
      this.loadingIncomeTypes = true;
      const data = new FormData();
      data.append("function", "getIncomeTypes");
      data.append("incomeTypePrefix", "PLANS");

      execute(data, biller)
          .then((res) => {
            this.loadingIncomeTypes = false;
            if (res.data.success && res.data.data?.incomeTypes) {
              this.incomeTypes = res.data.data.incomeTypes;
            }
          })
          .catch(() => {
            this.loadingIncomeTypes = false;
          });
    },

    loadFeesAndCharges(incomeTypeId) {
      this.loadingFeesAndCharges = true;
      this.feesAndCharges = [];
      this.selectedFeeAndCharge = null;

      const data = new FormData();
      data.append("function", "getFeesAndCharges");
      data.append("incomeTypeId", incomeTypeId);

      execute(data, biller)
          .then((res) => {
            this.loadingFeesAndCharges = false;
            if (res.data.success && res.data.data?.feesAndCharges) {
              this.feesAndCharges = res.data.data.feesAndCharges;
            }
          })
          .catch(() => {
            this.loadingFeesAndCharges = false;
          });
    },

    addBillItem() {
      if (!this.selectedIncomeType || !this.selectedFeeAndCharge) return;

      const incomeType = this.incomeTypes.find(t => String(t.incomeTypeId) === String(this.selectedIncomeType));
      const fee = this.feesAndCharges.find(f => String(f.feeId) === String(this.selectedFeeAndCharge));

      if (!incomeType || !fee) return;

      this.billItems.push({
        id: Date.now(),
        incomeTypeId: incomeType.incomeTypeId,
        incomeTypeDescription: incomeType.incomeTypeDescription,
        feeId: fee.feeId,
        feeDescription: fee.feeDescription,
        quantity: this.billingForm.quantity,
        unitAmount: fee.unitFeeAmount,
        totalAmount: parseFloat(this.billingForm.quantity) * parseFloat(fee.unitFeeAmount),
        currency: fee.currency || 'KES'
      });

      this.selectedFeeAndCharge = null;
      this.billingForm.quantity = 1;
      this.billingForm.amount = 0;
    },

    removeBillItem(itemId) {
      this.billItems = this.billItems.filter(i => i.id !== itemId);
    },

    calculateTotalAmount() {
      return this.billItems.reduce((total, item) => total + item.totalAmount, 0);
    },

    // Add Professional
    handleProfessionalFileChange(event) {
      this.professionalForm.certificationFile = event.target.files[0] || null;
    },

    addProfessional() {
      if (!this.professionalForm.professionalType || !this.professionalForm.certificateNumber ||
          !this.professionalForm.fullNames || !this.professionalForm.emailAddress ||
          !this.professionalForm.certificationExpiry) {
        this.submitStatus = 'error';
        this.submitMessage = 'Please fill all required fields';
        return;
      }

      this.isSubmitting = true;

      const data = new FormData();
      data.append("function", "addProfessional");
      Object.keys(this.professionalForm).forEach(key => {
        if (this.professionalForm[key]) {
          data.append(key, this.professionalForm[key]);
        }
      });

      execute(data, econstruction)
          .then((res) => {
            this.isSubmitting = false;
            if (res.data.success) {
              this.submitStatus = 'success';
              this.submitMessage = 'Professional added successfully';
              setTimeout(() => {
                $('#addProfessionalModal').modal('hide');
                this.resetProfessionalForm();
              }, 1500);
            } else {
              this.submitStatus = 'error';
              this.submitMessage = res.data.message || 'Failed to add professional';
            }
          })
          .catch((e) => {
            this.isSubmitting = false;
            this.submitStatus = 'error';
            this.submitMessage = e.message || 'An error occurred';
          });
    },

    resetProfessionalForm() {
      this.professionalForm = {
        professionalType: '',
        certificateNumber: '',
        fullNames: '',
        otherNames: '',
        phoneNumber: '',
        emailAddress: '',
        idNumber: '',
        certificationExpiry: '',
        certificationFile: null
      };
      this.submitMessage = '';
      this.submitStatus = null;
    },

    // Submit Application
    async submitApplication() {
      if (this.billItems.length === 0) {
        alert('Please add at least one billing item');
        return;
      }

      this.isSubmitting = true;

      try {
        // Generate Bill
        const billItems = this.billItems.map(item => ({
          fiscalYear: new Date().getFullYear(),
          typeDescription: item.incomeTypeDescription,
          feeDescription: item.feeDescription,
          quantity: item.quantity,
          unitFeeAmount: item.unitAmount,
          incomeTypeId: item.incomeTypeId,
          feeId: item.feeId,
          amount: item.totalAmount,
          customer: this.formData.plotDetails.LRNumber,
          zone: this.formData.plotDetails.zone,
          subCountyID: this.formData.plotDetails.subCounty,
          wardID: this.formData.plotDetails.ward,
          idNo: sessionStorage.getItem("sessionIdNo"),
          phoneNumber: sessionStorage.getItem("sessionPhoneNumber"),
          names: sessionStorage.getItem("sessionNames"),
          customerPhoneNumber: this.formData.plotDetails.ownerPhoneNumber,
          customerName: this.formData.plotDetails.ownerFullNames
        }));

        const billData = new FormData();
        billData.append("function", "generateBill3");
        billData.append("billItem", JSON.stringify(billItems));

        const billRes = await execute(billData, biller);

        if (!billRes.data.success) {
          throw new Error(billRes.data.message || 'Failed to generate bill');
        }

        const billNo = billRes.data.data.billGenerated.billNo;
        const billAmount = billRes.data.data.billGenerated.amount || 0;
        const printable = billRes.data.data.bill?.payBill?.printable === 'true';

        // Prepare documents array with fileId matching the FormData key
        // First, add PLUPA form as a document
        const documentsData = [];

        // Add PLUPA/DCIA form
        if (this.plupaForm.file) {
          documentsData.push({
            fileId: 'doc_plupa_dcia',
            documentType: 'plupaForm',
            documentNumber: '',
            expiryDate: '',
            fileName: this.plupaForm.fileName
          });
        }

        // Add other documents
        this.uploadedDocuments.forEach(doc => {
          documentsData.push({
            fileId: `doc_${doc.id}`,
            documentType: doc.documentType,
            documentNumber: doc.documentNumber,
            expiryDate: doc.expiryDate,
            fileName: doc.fileName
          });
        });

        // Submit Application
        const appData = new FormData();
        appData.append("function", "submitApplication");
        appData.append("applicationData", JSON.stringify({
          developmentType: this.formData.developmentType,
          projectTitle: this.formData.projectTitle,
          projectValue: this.formData.projectValue,
          projectDescription: this.formData.projectDescription,
          professionalId: this.formData.professionalId,
          plotNumber: this.formData.plotDetails.LRNumber,
          propertyId: this.formData.plotDetails.id,
          billNumber: billNo,
          sessionNames: sessionStorage.getItem("sessionNames"),
          sessionIdNo: sessionStorage.getItem("sessionIdNo"),
          plotDetails: this.formData.plotDetails,
          documents: documentsData
        }));

        // Append PLUPA form file
        if (this.plupaForm.file) {
          appData.append('doc_plupa_dcia', this.plupaForm.file);
        }

        // Append other files with matching keys
        this.uploadedDocuments.forEach(doc => {
          if (doc.file) {
            appData.append(`doc_${doc.id}`, doc.file);
          }
        });

        const appRes = await execute(appData, econstruction);

        this.isSubmitting = false;

        if (appRes.data.success) {
          // Store success data for modal
          this.successData = {
            applicationId: appRes.data.data.applicationId,
            applicationNumber: appRes.data.data.applicationNumber,
            billNo: billNo,
            billAmount: billAmount,
            printable: printable
          };

          // Show success modal
          const successModal = new bootstrap.Modal(document.getElementById('applicationSuccessModal'));
          successModal.show();
        } else {
          throw new Error(appRes.data.message || 'Failed to submit application');
        }
      } catch (error) {
        this.isSubmitting = false;
        alert('Error: ' + error.message);
      }
    },

    // Success Modal Methods
    formatDevelopmentType(type) {
      const types = {
        'subdivision': 'Subdivision/Amalgamation of land',
        'landUseChange': 'Change/Extension of Use of land',
        'leaseRenewal': 'Extension/Renewal of Lease',
        'advertising': 'Outdoor Advertising & Signage',
        'regularisation': 'Regularisation of Existing Developments',
        'structuralEngineering': 'Structural Engineering Designs',
        'civilEngineering': 'Civil Engineering Designs',
        'newBuildings': 'New Buildings',
        'buildingAlterations': 'Building Alterations/Additions',
        'demolition': 'Demolition of Dwellings',
        'architecturalRegularisation': 'Architectural Regularisation'
      };
      return types[type] || type;
    },

    printBillFromModal() {
      localStorage['params'] = JSON.stringify({ billNo: this.successData.billNo });
      window.open(this.$router.resolve({ name: 'bill' }).href, '_blank');
    },

    printApplicationReceipt() {
      // Just pass the application number - the receipt page will fetch full details from API
      localStorage['applicationReceipt'] = JSON.stringify({
        applicationNumber: this.successData.applicationNumber
      });
      window.open(this.$router.resolve({ name: 'application-receipt' }).href, '_blank');
    },

    closeSuccessAndReset() {
      // Hide modal
      const modalEl = document.getElementById('applicationSuccessModal');
      const modal = bootstrap.Modal.getInstance(modalEl);
      if (modal) modal.hide();

      // Reset form for new application
      this.resetForm();
    },

    startNewApplication() {
      // Hide modal
      const modalEl = document.getElementById('applicationSuccessModal');
      const modal = bootstrap.Modal.getInstance(modalEl);
      if (modal) modal.hide();

      // Reset form
      this.resetForm();

      // Scroll to top
      window.scrollTo(0, 0);
    },

    resetForm() {
      this.currentStep = 1;
      this.formData = {
        developmentType: '',
        projectTitle: '',
        projectValue: '',
        projectDescription: '',
        professionalType: '',
        certificateNumber: '',
        professionalFullNames: '',
        professionalOtherNames: '',
        professionalPhoneNumber: '',
        professionalEmail: '',
        professionalIdNumber: '',
        professionalId: null,
        plotDetails: {
          id: '', LRNumber: '', upnNumber: '', plotSize: '', currentBalance: '',
          landType: '', landUse: '', ownerFullNames: '', ownerIdNumber: '',
          ownerPhoneNumber: '', ownerEmail: '', ownerKraPin: '', ownerAddress: '',
          subCounty: '', ward: '', zone: ''
        }
      };
      this.selectedProfessionalId = null;
      this.professionalSearchResults = [];
      this.showProfessionalSelection = false;
      this.plotNumber = '';
      this.showPlotDetails = false;
      this.plotDetails = null;
      this.selectedPropertyId = null;
      this.propertySearchResults = [];
      this.showPropertySelection = false;
      this.plupaForm = { file: null, fileName: '', fileSize: 0, error: '' };
      this.plupaFormTouched = false;
      this.uploadedDocuments = [];
      this.billItems = [];
      this.selectedIncomeType = null;
      this.selectedFeeAndCharge = null;
      this.billingForm = { quantity: 1, amount: 0, details: '' };
      this.successData = { applicationId: '', applicationNumber: '', billNo: '', billAmount: 0, printable: false };
    }
  }
};
</script>

<style scoped>
.step-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.step-item.active .step-circle {
  box-shadow: 0 0 0 4px rgba(85, 110, 230, 0.2);
}

.step-item.completed .step-circle {
  box-shadow: 0 0 0 4px rgba(52, 195, 143, 0.2);
}

.step-content {
  min-height: 400px;
}

.card.border-dashed {
  border: 2px dashed #dee2e6 !important;
}

.avatar-md {
  width: 60px;
  height: 60px;
}

.avatar-title {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sticky-top {
  position: sticky;
  top: 0;
  z-index: 1;
}

.form-select-lg, .form-control-lg {
  font-size: 1rem;
}

/* Success Modal Animations */
#applicationSuccessModal .success-checkmark {
  animation: checkmark-pop 0.5s ease-out;
}

@keyframes checkmark-pop {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

#applicationSuccessModal .modal-header {
  border-radius: 0.5rem 0.5rem 0 0;
}

#applicationSuccessModal .modal-content {
  border-radius: 0.5rem;
  overflow: hidden;
}

#applicationSuccessModal h2 {
  font-weight: 700;
  letter-spacing: 1px;
}
</style>